import {getPosition} from './base.utils.js';

const sidebarInstances = new WeakMap();

export const followingSidebar = ({
  sidebar,
  contentSticky,
  sidebarInnerSelector = '.following-sidebar-inner',
  isHeaderCalc = false,
  gap = 130,
  bottomGap = 40,
} = {}) => {
  if (!sidebar || !contentSticky) return null;

  const existingInstance = sidebarInstances.get(sidebar);

  if (existingInstance) {
    existingInstance.refresh();
    return existingInstance;
  }

  const sidebarInner = sidebar.querySelector(sidebarInnerSelector);

  if (!sidebarInner) return null;

  const sidebarBuffer = document.createElement('div');
  const initialStyles = {
    position: sidebarInner.style.position,
    top: sidebarInner.style.top,
    bottom: sidebarInner.style.bottom,
  };
  const requestFrame = window.requestAnimationFrame?.bind(window) ?? window.setTimeout.bind(window);
  const cancelFrame = window.cancelAnimationFrame?.bind(window) ?? window.clearTimeout.bind(window);
  let frameId = null;
  let lastScrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  let isScrollingDown = true;
  let pendingScrollDirection;
  let shouldRefresh = false;
  let shouldSyncBuffer = false;
  let isResizeTransition = false;
  let resizeObserver = null;

  sidebarBuffer.classList.add('following-sidebar-buffer');
  sidebar.insertAdjacentElement('afterbegin', sidebarBuffer);
  sidebarInner.style.position = 'sticky';

  const headerHeight = () =>
    isHeaderCalc ? document.querySelector('header')?.offsetHeight ?? 0 : 0;

  const viewportHeight = () => window.visualViewport?.height ?? window.innerHeight;

  const updateBuffer = () => {
    const contentTop = getPosition(contentSticky).y;
    const sidebarTop = getPosition(sidebarInner).y;

    sidebarBuffer.style.height = `${Math.max(0, sidebarTop - contentTop)}px`;
  };

  const applyStickyConstraint = (scrollDirection, currentHeaderHeight) => {
    const sidebarHeight = sidebarInner.getBoundingClientRect().height;

    if (scrollDirection) {
      sidebarInner.style.top = `${Math.min(
        currentHeaderHeight + gap,
        viewportHeight() - sidebarHeight - bottomGap
      )}px`;
      sidebarInner.style.bottom = '';
    } else {
      sidebarInner.style.top = '';
      sidebarInner.style.bottom = `${
        viewportHeight() - sidebarHeight - currentHeaderHeight - gap
      }px`;
    }
  };

  const scheduleUpdate = ({syncBuffer = false} = {}) => {
    shouldSyncBuffer ||= syncBuffer;

    if (frameId !== null) return;

    frameId = requestFrame(() => {
      frameId = null;

      const nextScrollDirection = pendingScrollDirection;

      pendingScrollDirection = undefined;

      const currentHeaderHeight = headerHeight();

      if (
        nextScrollDirection !== undefined &&
        nextScrollDirection !== isScrollingDown
      ) {
        isScrollingDown = nextScrollDirection;
        updateBuffer();
        applyStickyConstraint(isScrollingDown, currentHeaderHeight);
        shouldRefresh = false;
        shouldSyncBuffer = false;
        return;
      }

      if (shouldRefresh) {
        if (shouldSyncBuffer) {
          updateBuffer();
        }

        applyStickyConstraint(isScrollingDown, currentHeaderHeight);
      }

      shouldRefresh = false;
      shouldSyncBuffer = false;
    });
  };

  const refresh = () => {
    shouldRefresh = true;
    scheduleUpdate({syncBuffer: true});
  };

  const refreshConstraint = () => {
    shouldRefresh = true;
    scheduleUpdate();
  };

  const beginResizeTransition = () => {
    isResizeTransition = true;
    refreshConstraint();
  };

  const endResizeTransition = () => {
    isResizeTransition = false;
    refresh();
  };

  const handleScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    const nextScrollDirection = scrollTop > lastScrollTop;
    const trackedScrollDirection = pendingScrollDirection ?? isScrollingDown;

    if (scrollTop !== lastScrollTop && nextScrollDirection !== trackedScrollDirection) {
      pendingScrollDirection = nextScrollDirection;
      scheduleUpdate();
    }

    lastScrollTop = Math.max(0, scrollTop);
  };

  applyStickyConstraint(isScrollingDown, headerHeight());

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (isResizeTransition) {
        refreshConstraint();
        return;
      }

      refresh();
    });
    resizeObserver.observe(sidebarInner);
    resizeObserver.observe(contentSticky);
  }

  window.addEventListener('scroll', handleScroll, {passive: true});
  window.addEventListener('resize', refresh, {passive: true});
  window.visualViewport?.addEventListener('resize', refresh, {passive: true});

  const destroy = () => {
    if (frameId !== null) cancelFrame(frameId);

    resizeObserver?.disconnect();
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', refresh);
    window.visualViewport?.removeEventListener('resize', refresh);
    sidebarBuffer.remove();
    sidebarInner.style.position = initialStyles.position;
    sidebarInner.style.top = initialStyles.top;
    sidebarInner.style.bottom = initialStyles.bottom;
    sidebarInstances.delete(sidebar);
  };

  const instance = {
    refresh,
    refreshConstraint,
    beginResizeTransition,
    endResizeTransition,
    destroy,
  };

  sidebarInstances.set(sidebar, instance);

  return instance;
};
